// TelcoTrack — sdílená logika rozsahu M/W/D pro manager KPI timeseries (globální dashboard + polygon).

export type ManagerDashboardGranularity = 'month' | 'week' | 'day';

export type ParsedManagerDashboardRange =
  | { ok: true; from: string; to: string; bucket: 'day' | 'week' | 'month' }
  | { ok: false; error: string };

export interface ManagerDashboardRangeState {
  granularity: ManagerDashboardGranularity;
  rangeStart: string;
  rangeEnd: string;
}

export const MANAGER_DASHBOARD_GRANULARITY_OPTIONS: Array<{
  id: ManagerDashboardGranularity;
  label: string;
  hint: string;
}> = [
  { id: 'month', label: 'M', hint: 'Měsíc — poslední 4 měsíce' },
  { id: 'week', label: 'W', hint: 'Týden — poslední 4 týdny' },
  { id: 'day', label: 'D', hint: 'Den — posledních 7 dní' },
];

export function rangeForGranularity(g: ManagerDashboardGranularity): {
  from: string;
  to: string;
  bucket: 'day' | 'week' | 'month';
} {
  const to = new Date();
  const toStr = to.toISOString().slice(0, 10);
  if (g === 'day') {
    const from = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate() - 6));
    return { from: from.toISOString().slice(0, 10), to: toStr, bucket: 'day' };
  }
  if (g === 'week') {
    const ref = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate() - 21));
    const mon = utcMonday(ref);
    return { from: mon.toISOString().slice(0, 10), to: toStr, bucket: 'week' };
  }
  const from = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() - 3, 1));
  return { from: from.toISOString().slice(0, 10), to: toStr, bucket: 'month' };
}

export function defaultRangeInputsForGranularity(g: ManagerDashboardGranularity): {
  rangeStart: string;
  rangeEnd: string;
} {
  const { from, to } = rangeForGranularity(g);
  if (g === 'day') {
    return { rangeStart: from, rangeEnd: to };
  }
  if (g === 'week') {
    return { rangeStart: toIsoWeekInput(from), rangeEnd: toIsoWeekInput(to) };
  }
  return { rangeStart: toMonthInput(from), rangeEnd: toMonthInput(to) };
}

export function parseManagerDashboardRange(state: ManagerDashboardRangeState): ParsedManagerDashboardRange {
  if (!state.rangeStart || !state.rangeEnd) {
    return { ok: false, error: 'Vyplňte začátek i konec období.' };
  }

  if (state.granularity === 'day') {
    const start = parseDateInput(state.rangeStart);
    const end = parseDateInput(state.rangeEnd);
    if (!start || !end) return { ok: false, error: 'Neplatný den v poli Od/Do.' };
    if (start.getTime() > end.getTime()) return { ok: false, error: 'Začátek období musí být před koncem.' };
    return { ok: true, from: toDateOnly(start), to: toDateOnly(end), bucket: 'day' };
  }

  if (state.granularity === 'week') {
    const start = parseIsoWeekInput(state.rangeStart);
    const end = parseIsoWeekInput(state.rangeEnd);
    if (!start || !end) return { ok: false, error: 'Neplatný týden v poli Od/Do.' };
    if (start.getTime() > end.getTime()) return { ok: false, error: 'Začátek období musí být před koncem.' };
    const endSunday = new Date(end.getTime());
    endSunday.setUTCDate(endSunday.getUTCDate() + 6);
    return { ok: true, from: toDateOnly(start), to: toDateOnly(endSunday), bucket: 'week' };
  }

  const startMonth = parseMonthInput(state.rangeStart);
  const endMonth = parseMonthInput(state.rangeEnd);
  if (!startMonth || !endMonth) return { ok: false, error: 'Neplatný měsíc v poli Od/Do.' };
  if (startMonth.getTime() > endMonth.getTime()) return { ok: false, error: 'Začátek období musí být před koncem.' };
  const endMonthLastDay = new Date(Date.UTC(endMonth.getUTCFullYear(), endMonth.getUTCMonth() + 1, 0));
  return { ok: true, from: toDateOnly(startMonth), to: toDateOnly(endMonthLastDay), bucket: 'month' };
}

export function getManagerDashboardRangeInputType(g: ManagerDashboardGranularity): 'date' | 'week' | 'month' {
  if (g === 'day') return 'date';
  if (g === 'week') return 'week';
  return 'month';
}

export function getManagerDashboardRangeStartLabel(g: ManagerDashboardGranularity): string {
  if (g === 'day') return 'Od dne';
  if (g === 'week') return 'Od týdne';
  return 'Od měsíce';
}

export function getManagerDashboardRangeEndLabel(g: ManagerDashboardGranularity): string {
  if (g === 'day') return 'Do dne';
  if (g === 'week') return 'Do týdne';
  return 'Do měsíce';
}

export function openNativeDatePicker(input: HTMLInputElement, event?: Event): void {
  event?.preventDefault();
  event?.stopPropagation();
  const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
  if (typeof pickerInput.showPicker === 'function') {
    pickerInput.showPicker();
    return;
  }
  input.focus();
}

function parseDateInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return new Date(Date.UTC(year, month - 1, day));
}

function parseMonthInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!year || month < 1 || month > 12) return null;
  return new Date(Date.UTC(year, month - 1, 1));
}

function parseIsoWeekInput(value: string): Date | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const week = Number(m[2]);
  if (!year || week < 1 || week > 53) return null;
  return isoWeekMonday(year, week);
}

function isoWeekMonday(year: number, week: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Weekday = jan4.getUTCDay() || 7;
  const week1Monday = new Date(Date.UTC(year, 0, 4 - jan4Weekday + 1));
  return new Date(Date.UTC(
    week1Monday.getUTCFullYear(),
    week1Monday.getUTCMonth(),
    week1Monday.getUTCDate() + (week - 1) * 7,
  ));
}

function toDateOnly(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function toMonthInput(dateOnly: string): string {
  const d = parseDateInput(dateOnly);
  if (!d) return '';
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function toIsoWeekInput(dateOnly: string): string {
  const d = parseDateInput(dateOnly);
  if (!d) return '';
  const thursday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const weekday = thursday.getUTCDay() || 7;
  thursday.setUTCDate(thursday.getUTCDate() + 4 - weekday);
  const weekYear = thursday.getUTCFullYear();
  const week1Monday = isoWeekMonday(weekYear, 1);
  const week = Math.floor((thursday.getTime() - week1Monday.getTime()) / 86400000 / 7) + 1;
  return `${weekYear}-W${String(week).padStart(2, '0')}`;
}

function utcMonday(d: Date): Date {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const wd = d.getUTCDay();
  const delta = (wd + 6) % 7;
  return new Date(Date.UTC(y, m, day - delta));
}
